import { useContext, useState } from 'react';
import { MainContext } from '../../contexts/main';
import { getText } from '@educaplay/app-core/language';
import { timestampToFullDate, timestampToLongDate } from '@educaplay/app-core/time';
import { RenewalText, declineReason, nextRetryKey, nextRetryValues, renewalPaymentMethodType, renewalValues } from '../../helpers/renewal';
import { PAYMENT_METHODS } from '../../helpers/constants';
import { SVG_alert, SVG_check, SVG_close, SVG_copy, SVG_info, SVG_xcircle } from '../elements/SVG';
import styles from './BlockRenewalNotice.module.scss';

export const BlockRenewalNotice = ({ renewal, payerMode = false }) => {

    const { mainReducer, environment } = useContext( MainContext );

    const [ stateCopied, setStateCopied ] = useState(false);

    const result = renewal.result;
    const pendingRenewal = (result === false) ? mainReducer.state.pendingRenewal : result.pendingRenewal;

    if (pendingRenewal === false) {
        return <></>;
    }

    const values = renewalValues(pendingRenewal, environment);
    const busy = (renewal.status != 'idle');
    const rejected = (result !== false) && (result.type == 'ko');
    const rejectedCard = rejected ? (result.decline.paymentMethod ?? pendingRenewal.paymentMethod) : false;
    const rejectedStoredCard = rejected && (rejectedCard?.id == pendingRenewal.paymentMethod?.id);

    const handleCopyLink = () => {
        navigator.clipboard.writeText(pendingRenewal.payerLink);
        setStateCopied(true);
    }

    const actionButton = (textKey, onClick, primary) =>
        <button className={ primary ? 'e-btn e-btn--premium e-btn--lg' : 'e-btn e-btn--link' } type="button" disabled={ busy } onClick={ onClick }>
            <span className="e-btn__txt">{ getText(textKey) }</span>
        </button>;

    const retryButton = (primary) => actionButton('renewal_retry_now', () => renewal.retry(pendingRenewal.paymentMethod.id), primary);

    const otherCardButton = (primary) => actionButton('renewal_use_other_card', renewal.choose, primary);

    if ((result !== false) && (result.type == 'ok')) {
        return  <div className="e-notice" role="status">
                    <span className="e-icon">
                        <SVG_check />
                    </span>
                    <div className="e-notice__wrapper">
                        <div className="e-notice__text">
                            <div className="e-notice__title">{ getText('renewal_retry_ok_title') }</div>
                            <div className="e-notice__body">
                                <RenewalText textKey={ payerMode ? 'renewal_retry_ok_body_payer' : 'renewal_retry_ok_body' } values={{ ...values, fecha: timestampToFullDate(result.end) }} />
                            </div>
                        </div>
                    </div>
                    <button className="e-notice__close" type="button" aria-label={ getText('cerrar') } onClick={ renewal.dismiss }>
                        <span className="e-icon">
                            <SVG_close />
                        </span>
                    </button>
                </div>;
    }

    if (pendingRenewal.sepaInProgress) {
        return  <div className="e-notice" role="status">
                    <span className="e-icon">
                        <SVG_info />
                    </span>
                    <div className="e-notice__wrapper">
                        <div className="e-notice__text">
                            <div className="e-notice__title"><RenewalText textKey="renewal_sepa_title" values={ values } /></div>
                            <div className="e-notice__body">
                                <RenewalText textKey="renewal_sepa_body" values={ values } />
                            </div>
                        </div>
                    </div>
                </div>;
    }

    const paymentMethodType = renewalPaymentMethodType(pendingRenewal, payerMode);
    let actions = false;
    if (!payerMode) {
        if (pendingRenewal.payer !== false) {
            actions = <>
                <button className="e-btn e-btn--secondary" type="button" onClick={ handleCopyLink }>
                    <span className="e-icon">
                        <SVG_copy />
                    </span>
                    <span className="e-btn__txt">{ getText(stateCopied ? 'renewal_payer_link_copied' : 'renewal_copy_payer_link') }</span>
                </button>
                { otherCardButton(false) }
            </>;
        } else if (!pendingRenewal.retryable) {
            actions = otherCardButton(true);
        } else if (rejected) {
            actions = <>
                { otherCardButton(true) }
                { retryButton(false) }
            </>;
        } else {
            actions = <>
                { retryButton(true) }
                { otherCardButton(false) }
            </>;
        }
    }

    return  <div className={ `e-notice ${styles.error}` } role="alert">
                <span className="e-icon">
                    { rejected ? <SVG_xcircle /> : <SVG_alert /> }
                </span>
                <div className="e-notice__wrapper">
                    <div className="e-notice__text">
                        <div className="e-notice__title">
                            { rejected ? getText('renewal_retry_ko_title') : <RenewalText textKey="renewal_pending_title" values={ values } /> }
                        </div>
                        <div className="e-notice__body">
                            { rejected ?
                                <RenewalText textKey={ (paymentMethodType == PAYMENT_METHODS.CARD) ? 'renewal_retry_ko_body_card' : 'renewal_retry_ko_body' } values={{ ...values, last4: rejectedCard?.description ?? '', motivo: declineReason(result.decline.family) }} />
                            : (pendingRenewal.lastDecline === false) ?
                                <RenewalText textKey="renewal_pending_body_no_decline" values={ values } />
                            :
                                <RenewalText textKey="renewal_pending_body" values={{ ...values, fecha: timestampToLongDate(pendingRenewal.lastDecline.date), motivo: declineReason(pendingRenewal.lastDecline.family) }} />
                            }
                        </div>
                        <ul className={ styles.steps }>
                            { pendingRenewal.lastAttempt ?
                                <li><RenewalText textKey="renewal_pending_last_attempt" values={{ fecha: timestampToLongDate(pendingRenewal.nextRetryDate) }} /></li>
                            :
                                <>
                                    <li><RenewalText textKey={ nextRetryKey(pendingRenewal, payerMode, rejectedStoredCard) } values={ nextRetryValues(pendingRenewal) } /></li>
                                    <li><RenewalText textKey="renewal_pending_loss_date" values={{ fecha: timestampToLongDate(pendingRenewal.lossDate) }} /></li>
                                </>
                            }
                        </ul>
                        { (!payerMode) && (pendingRenewal.payer !== false) &&
                            <div className="e-notice__body">
                                <RenewalText textKey="renewal_pending_payer" values={{ pagador: pendingRenewal.payer }} />
                            </div>
                        }
                        { (actions !== false) &&
                            <div className={ styles.actions }>
                                { actions }
                            </div>
                        }
                    </div>
                </div>
            </div>;
}
